import { CAMPO } from "./ui";
import { BOTAO_SECUNDARIO, IconeMais, Rotulo } from "./PeritoUi";
import { hoje } from "../lib/perito/datas";
import { formatBRL, PARTE_LABEL } from "../lib/perito/format";
import type { Honorario, Parte } from "../lib/perito/tipos";

// Os dois editores que o cadastro e a ficha usam, portados de `src/components/editors/` da Central do
// Perito (000385). Os ids saem de `crypto.randomUUID()`: o honorário vira linha de `perito_honorarios`, cuja
// chave é uuid, e o id de lá (`Math.random` em base 36) seria recusado pelo banco.

const TIPOS_DE_PARTE = Object.keys(PARTE_LABEL) as Parte["tipo"][];

/** O número digitado num campo de valor: vazio é zero, e o negativo não existe (o banco recusa). */
function valorDoCampo(v: string): number {
  const n = Number.parseFloat(v.replace(",", "."));
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : 0;
}

export function PartesEditor({ valor, aoMudar }: { valor: Parte[]; aoMudar: (v: Parte[]) => void }) {
  const add = () => aoMudar([...valor, { id: crypto.randomUUID(), nome: "", tipo: "autor", contato: "" }]);
  const mudar = (id: string, patch: Partial<Parte>) => aoMudar(valor.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  const tirar = (id: string) => aoMudar(valor.filter((p) => p.id !== id));
  return (
    <div className="flex flex-col gap-3" data-editor-partes>
      {valor.map((p) => (
        // Uma linha por parte: em grade no desktop, empilhada no celular, como lá.
        <div key={p.id} className="grid grid-cols-[minmax(0,1fr)_2.5rem] items-center gap-2 rounded-xl border border-borda bg-fundo p-2.5 sm:grid-cols-[10rem_minmax(0,1.6fr)_minmax(0,1fr)_2.5rem] sm:gap-3" data-parte>
          <select className={`${CAMPO} col-span-2 sm:col-span-1`} value={p.tipo} aria-label="Tipo da parte"
            onChange={(e) => mudar(p.id, { tipo: e.target.value as Parte["tipo"] })}>
            {TIPOS_DE_PARTE.map((t) => <option key={t} value={t}>{PARTE_LABEL[t]}</option>)}
          </select>
          <input className={`${CAMPO} col-span-2 sm:col-span-1`} value={p.nome} maxLength={200} placeholder="Nome completo"
            aria-label="Nome da parte" onChange={(e) => mudar(p.id, { nome: e.target.value })} />
          <input className={CAMPO} value={p.contato ?? ""} maxLength={200} placeholder="CPF, e-mail ou telefone"
            aria-label="Contato da parte" onChange={(e) => mudar(p.id, { contato: e.target.value })} />
          <button type="button" onClick={() => tirar(p.id)} aria-label={`Remover ${p.nome || "a parte"}`}
            className="grid size-10 place-items-center rounded-full text-cinza hover:bg-chip hover:text-erro-tinta">×</button>
        </div>
      ))}
      {valor.length === 0 && <p className="py-3 text-center text-sm text-cinza">Nenhuma parte cadastrada.</p>}
      <button type="button" className={`${BOTAO_SECUNDARIO} self-start`} onClick={add} data-adicionar-parte><IconeMais /> Adicionar parte</button>
    </div>
  );
}

const SITUACAO_LABEL: Record<Honorario["status"], string> = {
  pendente: "Ainda não recebido",
  parcial: "Recebido em parte",
  pago: "Recebido integralmente",
};

/** As regras do lançamento, as de lá: "pago" recebe o valor inteiro, "pendente" zera o recebido e a data. */
export function ajustarHonorario(h: Honorario, patch: Partial<Honorario>): Honorario {
  const m = { ...h, ...patch };
  if (patch.status === "pago") m.recebido = m.valor;
  if (patch.status === "pendente") {
    m.recebido = 0;
    m.dataRecebimento = undefined;
  }
  // Recebeu alguma coisa e a data não foi dita: o dia de hoje, em São Paulo (lá, o dia de UTC).
  if ((m.recebido || 0) > 0 && !m.dataRecebimento && patch.dataRecebimento === undefined) m.dataRecebimento = hoje();
  return m;
}

export function HonorariosEditor({ valor, aoMudar }: { valor: Honorario[]; aoMudar: (v: Honorario[]) => void }) {
  const add = () => aoMudar([...valor, { id: crypto.randomUUID(), descricao: "Honorários periciais", valor: 0, recebido: 0, status: "pendente" }]);
  const mudar = (id: string, patch: Partial<Honorario>) => aoMudar(valor.map((h) => (h.id === id ? ajustarHonorario(h, patch) : h)));
  const tirar = (id: string) => aoMudar(valor.filter((h) => h.id !== id));
  const total = valor.reduce((a, h) => a + (h.valor || 0), 0);
  const recebido = valor.reduce((a, h) => a + (h.recebido || 0), 0);
  return (
    <div className="flex flex-col gap-4" data-editor-honorarios>
      <p className="text-sm leading-relaxed text-cinza">
        Registre os honorários já definidos no processo. Se ainda não foram arbitrados, pule este passo: dá para lançá-los depois, na ficha da perícia.
      </p>
      {valor.map((h, i) => (
        <div key={h.id} className="flex flex-col gap-4 rounded-cf-lg border border-borda bg-fundo p-4" data-honorario>
          <div className="flex items-center justify-between gap-2">
            <Rotulo>Lançamento {i + 1}</Rotulo>
            <button type="button" onClick={() => tirar(h.id)} className="min-h-9 rounded-full px-3 text-sm font-semibold text-erro-tinta hover:bg-erro-fundo">Remover</button>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm font-semibold text-tinta">Descrição
              <input className={CAMPO} value={h.descricao} maxLength={300} placeholder="Ex.: Honorários periciais"
                onChange={(e) => mudar(h.id, { descricao: e.target.value })} />
            </label>
            <label className="flex flex-col gap-1 text-sm font-semibold text-tinta">Valor (R$)
              <input className={`${CAMPO} tabular-nums`} type="number" step="0.01" min="0" inputMode="decimal" placeholder="0,00"
                value={h.valor || ""} onChange={(e) => mudar(h.id, { valor: valorDoCampo(e.target.value) })} data-valor />
            </label>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm font-semibold text-tinta">Previsão de depósito
              <input className={CAMPO} type="date" value={h.vencimento ?? ""} onChange={(e) => mudar(h.id, { vencimento: e.target.value || undefined })} />
            </label>
            <label className="flex flex-col gap-1 text-sm font-semibold text-tinta">Situação
              <select className={CAMPO} value={h.status} onChange={(e) => mudar(h.id, { status: e.target.value as Honorario["status"] })} data-situacao>
                {(Object.keys(SITUACAO_LABEL) as Honorario["status"][]).map((st) => <option key={st} value={st}>{SITUACAO_LABEL[st]}</option>)}
              </select>
            </label>
          </div>
          {h.status !== "pendente" && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="flex flex-col gap-1 text-sm font-semibold text-tinta">Valor recebido (R$)
                <input className={`${CAMPO} tabular-nums`} type="number" step="0.01" min="0" inputMode="decimal" placeholder="0,00"
                  value={h.recebido || ""} onChange={(e) => mudar(h.id, { recebido: valorDoCampo(e.target.value) })} />
              </label>
              <label className="flex flex-col gap-1 text-sm font-semibold text-tinta">Data do recebimento
                <input className={CAMPO} type="date" value={h.dataRecebimento ?? ""} onChange={(e) => mudar(h.id, { dataRecebimento: e.target.value || undefined })} />
              </label>
            </div>
          )}
        </div>
      ))}
      {valor.length === 0 && <p className="rounded-cf-lg border border-dashed border-borda py-4 text-center text-sm text-cinza">Nenhum honorário lançado.</p>}
      <button type="button" className={`${BOTAO_SECUNDARIO} self-start`} onClick={add} data-adicionar-honorario><IconeMais /> Adicionar honorário</button>
      {valor.length > 0 && (
        <dl className="grid grid-cols-3 gap-3 border-t border-borda pt-4">
          <div><dt className="text-xs text-cinza">Total</dt><dd className="font-display font-bold text-titulo tabular-nums">{formatBRL(total)}</dd></div>
          <div><dt className="text-xs text-cinza">Recebido</dt><dd className="font-display font-bold text-aviso-tinta tabular-nums">{formatBRL(recebido)}</dd></div>
          <div><dt className="text-xs text-cinza">A receber</dt><dd className="font-display font-bold text-alerta-tinta tabular-nums">{formatBRL(total - recebido)}</dd></div>
        </dl>
      )}
    </div>
  );
}
